import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, UserIcon, MapPinIcon, LoaderIcon, AlertCircleIcon, PhoneIcon } from 'lucide-react';
interface KDesignersQuoteFormProps {
  initialZip: string;
  serviceSlug: string;
  onClose: () => void;
}
export function KDesignersQuoteForm({
  initialZip,
  serviceSlug,
  onClose
}: KDesignersQuoteFormProps) {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [zipLookupLoading, setZipLookupLoading] = useState(false);
  const [zipLookupDone, setZipLookupDone] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [geoError, setGeoError] = useState('');
  const [editingZip, setEditingZip] = useState(false);
  const getProductId = (slug: string): string => {
    switch (slug) {
      case 'bathroom':
        return 'Bath';
      case 'windows':
        return 'Win';
      case 'siding':
        return 'Sid';
      case 'doors':
        return 'Door';
      // Assuming 'Door' based on pattern, though not explicitly in spec
      default:
        return 'Win';
    }
  };
  const getProductDesc = (slug: string): string => {
    switch (slug) {
      case 'bathroom':
        return 'Bath Remodel';
      case 'windows':
        return 'Windows';
      case 'siding':
        return 'Siding';
      case 'doors':
        return 'Entry Doors';
      default:
        return 'Windows';
    }
  };
  const getServiceLabel = (slug: string): string => {
    switch (slug) {
      case 'bathroom':
        return 'Bathroom Remodel';
      case 'windows':
        return 'Windows';
      case 'siding':
        return 'Siding';
      case 'doors':
        return 'Entry Doors';
      default:
        return 'Home Improvement';
    }
  };
  // Market mapping based on the provided image
  const getMarketCode = (city: string, state: string): string => {
    const location = `${city}, ${state}`.toLowerCase();
    if (location.includes('sacramento')) return '40';
    if (location.includes('denver')) return '30';
    if (location.includes('portland')) return '42';
    if (location.includes('salt lake')) return '43';
    if (location.includes('minneapolis')) return '52';
    if (location.includes('des moines')) return '46';
    if (location.includes('omaha')) return '44';
    if (location.includes('billings')) return '51';
    if (location.includes('cheyenne')) return '31';
    if (location.includes('twin falls')) return '47';
    if (location.includes('duluth')) return '53';
    if (location.includes('san jose')) return '48';
    return '00'; // Default/unknown market
  };
  const [formData, setFormData] = useState({
    alid: 'KDES_ALID',
    oid: 'KDES_OID',
    firstname: '',
    lastname: '',
    address1: '',
    city: '',
    state: '',
    zip: initialZip || '',
    email: '',
    phone: '',
    phonetype: '1',
    productid: getProductId(serviceSlug),
    sender: 'BirdDog',
    Product: getProductDesc(serviceSlug),
    srs_id: 'KDES_SRS',
    trustedform_cert_url: '',
    user2: '',
    Market: '',
    ip: '',
    datereceived: '',
    Company: 'KDES_COMP',
    lognumber: ''
  });
  // Get IP address on load
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => {
      setFormData(prev => ({
        ...prev,
        ip: data.ip
      }));
    }).catch(err => console.error('Error fetching IP:', err));
  }, []);
  // Zip code lookup
  useEffect(() => {
    const zip = formData.zip;
    if (zip && zip.length === 5) {
      setZipLookupLoading(true);
      setZipLookupDone(false);
      setGeoError('');
      fetch(`https://api.zippopotam.us/us/${zip}`).then(res => {
        if (!res.ok) throw new Error('Invalid zip');
        return res.json();
      }).then(data => {
        if (data.places && data.places.length > 0) {
          const place = data.places[0];
          const stateAbbr = place['state abbreviation'];
          const cityName = place['place name'];
          setFormData(prev => ({
            ...prev,
            city: cityName || '',
            state: stateAbbr || '',
            Market: getMarketCode(cityName, stateAbbr)
          }));
          setZipLookupDone(true);
        }
      }).catch(() => {
        setZipLookupDone(false);
      }).finally(() => {
        setZipLookupLoading(false);
      });
    }
  }, [formData.zip]);
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({
      ...prev,
      phone: digits
    }));
  };
  // TrustedForm
  useEffect(() => {
    try {
      const tf = document.createElement('script');
      tf.type = 'text/javascript';
      tf.async = true;
      tf.src = 'https://api.trustedform.com/trustedform.js?provide_referrer=false&field=xxTrustedFormCertUrl&l=' + new Date().getTime() + Math.random();
      document.head.appendChild(tf);
    } catch (e) {
      console.warn('TrustedForm script could not be loaded:', e);
    }
    const interval = setInterval(() => {
      try {
        const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
        if (certInput && certInput.value) {
          setFormData(prev => ({
            ...prev,
            trustedform_cert_url: certInput.value
          }));
          clearInterval(interval);
        }
      } catch (e) {
        // continue polling
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const totalSteps = 2; // Address, Contact Info
  const progress = step / totalSteps * 100;
  const handleNext = () => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };
  const handleBack = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  };
  const validateAddress = (address: string): boolean => {
    const addressPattern = /^\d+\s+\S{2,}/;
    return addressPattern.test(address.trim());
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    // Name validation: no numeric characters
    if ((name === 'firstname' || name === 'lastname') && /\d/.test(value)) {
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'address1') {
      setAddressError('');
    }
  };
  const handleAddressBlur = () => {
    if (formData.address1 && !validateAddress(formData.address1)) {
      setAddressError('Please enter a valid street address (e.g. 123 Main St)');
    } else {
      setAddressError('');
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
    const finalCert = certInput?.value || formData.trustedform_cert_url;
    // Format date as MM/DD/YYYY HH:MM:SS
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const formattedDate = `${pad(now.getMonth() + 1)}/${pad(now.getDate())}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    const payload = {
      ...formData,
      trustedform_cert_url: finalCert,
      datereceived: formattedDate
    };
    // Submit to BDM lead management
    const bdmParams = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (value) bdmParams.append(key, String(value));
    });
    const bdmUrl = `http://bdmleadmanagement.valid1.net/Leads.aspx?${bdmParams.toString()}`;
    console.log('Submitting to K-Designers BDM:', bdmUrl);
    try {
      await fetch(bdmUrl, {
        method: 'POST',
        mode: 'no-cors'
      });
    } catch (err) {
      console.warn('BDM post failed, continuing:', err);
    }
    setIsSubmitting(false);
    setIsSuccess(true);
  };
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir < 0 ? 50 : -50,
      opacity: 0
    })
  };
  const Header = ({
    hideExit = false
  }: {
    hideExit?: boolean;
  }) => <header className="bg-white border-b border-gray-200 py-4 px-6 flex justify-between items-center sticky top-0 z-10" data-id="element-1701">
      <div className="flex flex-col" data-id="element-1702">
        <div className="flex items-baseline gap-2" data-id="element-1703">
          <img src="https://cdn.magicpatterns.com/uploads/idrKUKYXCqVbysWS3F6st9/K_Designers_Logo.png" alt="K-Designers" className="h-10 md:h-12 object-contain" data-id="element-1704" />
        </div>
        <span className="text-[10px] md:text-xs font-semibold text-gray-600" data-id="element-1705">
          {getServiceLabel(serviceSlug)} Estimate
        </span>
      </div>
      <div className="flex items-center gap-6" data-id="element-1706">
        {!hideExit && <button onClick={onClose} className="text-sm font-medium text-gray-500 hover:text-[#00B4D8] transition-colors flex items-center gap-1" data-id="element-1707">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-1708" />
            <span className="hidden sm:inline" data-id="element-1709">Exit</span>
          </button>}
      </div>
    </header>;
  if (isSuccess) {
    return <div className="min-h-screen bg-white flex flex-col font-sans" data-id="element-1710">
        <Header hideExit data-id="element-1711" />

        {/* Hero Banner */}
        <div className="relative w-full h-64 md:h-80 flex items-center" data-id="element-1712">
          <div className="absolute inset-0 z-0" data-id="element-1713">
            <img src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Home improvement professional" className="w-full h-full object-cover" data-id="element-1714" />
          </div>
          <div className="absolute inset-0 z-10" data-id="element-1715">
            <div className="absolute left-0 top-0 bottom-0 w-full md:w-2/3 bg-[#1B2A4A]/90 flex flex-col justify-center px-8 md:px-16" data-id="element-1716">
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight" data-id="element-1717">
                Your Quote Request Has
                <br data-id="element-1718" />
                Been Submitted!
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-1719">
                A K-Designers representative will be calling you shortly.
              </p>
            </div>
          </div>
        </div>

        {/* Keep Your Phone Nearby Section */}
        <div className="max-w-3xl mx-auto w-full px-6 py-16 text-center" data-id="element-1720">
          <div className="flex justify-center mb-6" data-id="element-1721">
            <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-1722">
              <PhoneIcon className="w-10 h-10 text-[#00B4D8]" data-id="element-1723" />
            </div>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-[#1B2A4A] mb-6" data-id="element-1724">
            Keep your phone nearby!
          </h2>
          <p className="text-gray-600 leading-relaxed max-w-xl mx-auto" data-id="element-1725">
            A K-Designers representative will be reaching out to you at{' '}
            <span className="font-semibold text-gray-900" data-id="element-1726">
              {formData.phone}
            </span>{' '}
            to discuss your {getServiceLabel(serviceSlug).toLowerCase()} project
            and schedule your free in-home estimate. The call typically comes
            within one business day.
          </p>
        </div>

        {/* Homeowner Info */}
        <div className="max-w-4xl mx-auto w-full px-6 py-12 text-center border-b border-gray-200" data-id="element-1727">
          <h2 className="text-[#00B4D8] font-bold text-sm uppercase tracking-wider mb-6" data-id="element-1728">
            Your Submission Details
          </h2>
          <div className="text-gray-900 text-lg mb-2" data-id="element-1729">
            <p className="font-semibold text-xl" data-id="element-1730">
              {formData.firstname} {formData.lastname}
            </p>
            <p data-id="element-1731">{formData.address1}</p>
            <p data-id="element-1732">
              {formData.city}, {formData.state} {formData.zip}
            </p>
            <p data-id="element-1733">{formData.phone}</p>
            <p className="text-gray-500" data-id="element-1734">{formData.email}</p>
          </div>

          <div className="mt-6 inline-flex flex-col gap-2 text-sm text-gray-600" data-id="element-1735">
            <div className="flex items-center justify-center gap-2" data-id="element-1736">
              <span className="font-semibold text-gray-800" data-id="element-1737">Project Type:</span>
              <span data-id="element-1738">{getServiceLabel(serviceSlug)}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-[#1B2A4A] py-16 mt-auto" data-id="element-1739">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1740">
            <p className="text-white/80 text-sm" data-id="element-1741">
              &copy; {new Date().getFullYear()} findservicequotes.io. All Rights
              Reserved.
            </p>
          </div>
        </footer>
      </div>;
  }
  return <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-id="element-1742">
      <Header data-id="element-1743" />
      <input type="hidden" name="xxTrustedFormCertUrl" data-id="element-1744" />

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 h-2" data-id="element-1745">
        <motion.div className="bg-[#00B4D8] h-full" initial={{
        width: 0
      }} animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-1746" />
      </div>

      <main className="flex-1 flex flex-col items-center pt-8 pb-20 px-4" data-id="element-1747">
        <div className="w-full max-w-2xl" data-id="element-1748">
          <div className="mb-6 h-8" data-id="element-1749">
            {step > 1 && <button onClick={handleBack} className="text-gray-500 hover:text-[#00B4D8] font-medium flex items-center gap-2 transition-colors" data-id="element-1750">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-1751" /> Back
              </button>}
          </div>

          <div className="relative bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-gray-100" data-id="element-1752">
            <AnimatePresence mode="wait" custom={direction} data-id="element-1753">
              {/* STEP 1: Address */}
              {step === 1 && <motion.div key="step1" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-1754">
                  <h1 className="text-2xl md:text-3xl font-bold text-[#1B2A4A] mb-8 text-center" data-id="element-1755">
                    Where is your project located?
                  </h1>

                  <div className="space-y-5" data-id="element-1756">
                    <div data-id="element-1757">
                      <label htmlFor="address1" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-1758">
                        Street Address
                      </label>
                      <input type="text" id="address1" name="address1" value={formData.address1} onChange={handleInputChange} onBlur={handleAddressBlur} className={`w-full h-14 px-4 bg-gray-50 border rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent ${addressError ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} placeholder="123 Main St" data-id="element-1759" />
                      {addressError && <p className="text-red-500 text-xs mt-1.5 font-medium" data-id="element-1760">
                          {addressError}
                        </p>}

                      <div className="mt-3 flex flex-col gap-2" data-id="element-1761">
                        {zipLookupLoading ? <div className="flex items-center gap-2 text-gray-400 text-sm" data-id="element-1762">
                            <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1763" />
                            <span data-id="element-1764">Looking up location...</span>
                          </div> : editingZip ? <div className="flex items-center gap-2 w-full" data-id="element-1765">
                            <MapPinIcon className="w-4 h-4 text-[#00B4D8] flex-shrink-0" data-id="element-1766" />
                            <input type="text" value={formData.zip} onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setFormData(prev => ({
                          ...prev,
                          zip: val,
                          city: '',
                          state: '',
                          Market: ''
                        }));
                        setZipLookupDone(false);
                        setGeoError('');
                      }} onBlur={() => {
                        if (formData.zip.length === 5) setEditingZip(false);
                      }} onKeyDown={e => {
                        if (e.key === 'Enter' && formData.zip.length === 5) setEditingZip(false);
                      }} placeholder="Enter ZIP" maxLength={5} autoFocus className="flex-1 h-10 px-3 bg-white border border-blue-300 rounded-lg text-sm font-semibold text-[#00B4D8] focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent" data-id="element-1767" />
                          </div> : zipLookupDone && formData.city && formData.state ? <button type="button" onClick={() => setEditingZip(true)} className={`flex items-center gap-2 border rounded-lg px-3 py-2 w-full transition-colors cursor-pointer text-left ${geoError ? 'bg-red-50 border-red-200 hover:bg-red-100' : 'bg-blue-50 border-blue-200 hover:bg-blue-100'}`} title="Click to change ZIP code" data-id="element-1768">
                            <MapPinIcon className={`w-4 h-4 flex-shrink-0 ${geoError ? 'text-red-500' : 'text-[#00B4D8]'}`} data-id="element-1769" />
                            <span className={`text-sm font-semibold ${geoError ? 'text-red-700' : 'text-[#00B4D8]'}`} data-id="element-1770">
                              {formData.city}, {formData.state} {formData.zip}
                            </span>
                            <span className="text-[10px] text-gray-400 ml-auto" data-id="element-1771">
                              edit
                            </span>
                          </button> : <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 w-full hover:bg-gray-100 transition-colors cursor-pointer text-left" title="Click to enter ZIP code" data-id="element-1772">
                            <MapPinIcon className="w-4 h-4 text-gray-400" data-id="element-1773" />
                            <span className="text-sm text-gray-500" data-id="element-1774">
                              {formData.zip ? `Zip: ${formData.zip}` : 'Enter your ZIP code'}
                            </span>
                          </button>}

                        {geoError && <div className="flex items-start gap-2 text-red-600 bg-red-50 p-3 rounded-lg border border-red-100 mt-2" data-id="element-1775">
                            <AlertCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-1776" />
                            <p className="text-sm font-medium" data-id="element-1777">{geoError}</p>
                          </div>}
                      </div>
                    </div>

                    <button onClick={() => {
                  if (!validateAddress(formData.address1)) {
                    setAddressError('Please enter a valid street address (e.g. 123 Main St)');
                    return;
                  }
                  handleNext();
                }} disabled={!formData.address1 || !formData.city || !formData.state || !formData.zip || !!addressError || !!geoError} className="w-full mt-6 h-14 bg-[#00B4D8] hover:bg-[#0096b4] text-white font-bold text-lg rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1778">
                      Continue
                    </button>
                  </div>
                </motion.div>}

              {/* STEP 2: Contact Information */}
              {step === 2 && <motion.div key="step2" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-1779">
                  <div className="flex justify-center mb-6" data-id="element-1780">
                    <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center" data-id="element-1781">
                      <UserIcon className="w-8 h-8 text-[#00B4D8]" data-id="element-1782" />
                    </div>
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold text-[#1B2A4A] mb-2 text-center" data-id="element-1783">
                    Who should we contact?
                  </h1>
                  <p className="text-gray-500 text-center mb-8" data-id="element-1784">
                    Your free estimate is almost ready.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1785">
                    <div className="grid grid-cols-2 gap-4" data-id="element-1786">
                      <div data-id="element-1787">
                        <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-1788">
                          First Name
                        </label>
                        <input type="text" name="firstname" value={formData.firstname} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent" required data-id="element-1789" />
                      </div>
                      <div data-id="element-1790">
                        <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-1791">
                          Last Name
                        </label>
                        <input type="text" name="lastname" value={formData.lastname} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent" required data-id="element-1792" />
                      </div>
                    </div>

                    <div data-id="element-1793">
                      <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-1794">
                        Phone Number
                      </label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handlePhoneChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent" placeholder="(555) 555-5555" required maxLength={10} data-id="element-1795" />
                    </div>

                    <div data-id="element-1796">
                      <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-1797">
                        Email Address
                      </label>
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent" required data-id="element-1798" />
                    </div>

                    <div className="mt-6" data-id="element-1799">
                      <label className="flex items-start gap-3 cursor-pointer group" data-id="element-1800">
                        <div className="relative flex items-start pt-1" data-id="element-1801">
                          <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="peer sr-only" data-id="element-1802" />
                          <div className="w-5 h-5 border-2 border-gray-300 rounded bg-white peer-checked:bg-[#00B4D8] peer-checked:border-[#00B4D8] transition-colors flex items-center justify-center" data-id="element-1803">
                            <CheckCircle2Icon className="w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100" data-id="element-1804" />
                          </div>
                        </div>
                        <span className="text-[11px] leading-tight text-gray-500 select-none" data-id="element-1805">
                          By clicking "Get My Free Quote", I agree to the Terms
                          of Service and Privacy Policy. I authorize K-Designers
                          and its partners to contact me at the phone number and
                          email provided, including via automated technology,
                          text messages, and prerecorded voice. Consent is not a
                          condition of purchase.
                        </span>
                      </label>
                    </div>

                    <button type="submit" disabled={isSubmitting || !formData.firstname || !formData.lastname || formData.phone.length < 10 || !formData.email || !consentChecked} className="w-full mt-6 h-16 bg-[#FF6F00] hover:bg-orange-700 text-white font-bold text-xl rounded-xl transition-all shadow-lg shadow-orange-500/30 hover:shadow-orange-500/40 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2" data-id="element-1806">
                      {isSubmitting ? <>
                          <LoaderIcon className="w-6 h-6 animate-spin" data-id="element-1807" />
                          Submitting...
                        </> : 'Get My Free Quote'}
                    </button>
                  </form>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>;
}